/**
 * Хүлээн авалт — агуулахаас салбар руу илгээсэн барааг хүлээн авах.
 *
 * Гутал бүрийг уншуулахгүй (2026-10-03): шилжүүлгийг сонгоод «Хүлээн
 * авах» → бүх гутал салбарын нөөцөд НЭГ транзакцад орно. Ирээгүй гутал
 * байвал тэмдэглэгээг нь арилгана — тэр нь замд үлдэж, шилжүүлэг
 * «Дутуу ирсэн» болно (`POST /transfers/:id/receive`, `all: true`).
 *
 * Баталгаажсаны дараа баримтыг Excel (.xlsx) файлаар татна —
 * `GET /transfers/:id/receipt`.
 *
 * Гар утасны уншуулж хүлээн авах зам (`features/store/receive.tsx`)
 * хэвээр — хоёулаа ижил endpoint.
 */

import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { formatDateTime, formatQty, formatSerialDisplay, formatSize } from '@boots/domain';
import { api, download, type Envelope, type PagedEnvelope } from '../lib/api';
import { useAuth } from '../lib/auth';
import type { TransferDetail, TransferRow } from '../lib/types';
import {
  Button, Empty, ErrorNote, Field, Input, Modal, Money, OkNote, Spinner, Tabs, Td, Th,
  TransferStatusBadge,
} from '../components/ui';
import { DataTable, type Column } from '../components/data-table';

type State = 'incoming' | 'done';

interface ReceiveResult {
  received: number;
  locationName: string;
}

export function ReceivingPage() {
  const [state, setState] = useState<State>('incoming');
  const [openId, setOpenId] = useState<string | null>(null);

  const inbound = (s: State) => ({
    queryKey: ['inbound', s],
    queryFn: () => api<PagedEnvelope<TransferRow>>(`/transfers/inbound?state=${s}&limit=100`),
  });
  const incoming = useQuery(inbound('incoming'));
  const done = useQuery(inbound('done'));
  const list = state === 'incoming' ? incoming : done;

  return (
    <div className="flex flex-col gap-3">
      <Tabs
        value={state}
        onChange={setState}
        tabs={[
          { key: 'incoming', label: 'Ирж буй', count: incoming.data?.meta.total },
          { key: 'done', label: 'Хүлээн авсан', count: done.data?.meta.total },
        ]}
      />

      {list.error ? <ErrorNote>{(list.error as Error).message}</ErrorNote> : (
        <section className="overflow-hidden rounded-table sm:border sm:border-hair sm:bg-panel">
          <DataTable
            rows={list.data?.data ?? []}
            loading={list.isPending}
            columns={state === 'incoming' ? incomingColumns() : doneColumns()}
            rowKey={(t) => t.id}
            onRowClick={(t) => setOpenId(t.id)}
            empty={<Empty>{state === 'incoming' ? 'Ирж буй бараа алга' : 'Хүлээн авсан шилжүүлэг алга'}</Empty>}
          />
        </section>
      )}

      {openId ? <InboundModal id={openId} onClose={() => setOpenId(null)} /> : null}
    </div>
  );
}

// ------------------------------------------------------------ Дэлгэрэнгүй
function InboundModal({ id, onClose }: { id: string; onClose: () => void }) {
  const { readOnly } = useAuth();
  const qc = useQueryClient();
  const [missing, setMissing] = useState<ReadonlySet<string>>(new Set());
  const [note, setNote] = useState('');
  const [result, setResult] = useState<string | null>(null);

  const q = useQuery({
    queryKey: ['inbound', id],
    queryFn: () => api<Envelope<TransferDetail>>(`/transfers/inbound/${id}`),
  });

  const receive = useMutation({
    mutationFn: () =>
      api<Envelope<ReceiveResult>>(`/transfers/${id}/receive`, {
        method: 'POST',
        body: { all: true, missingItemIds: [...missing], ...(note.trim() ? { note: note.trim() } : {}) },
      }),
    onSuccess: async (res) => {
      setResult(`${formatQty(res.data.received)} гутал «${res.data.locationName}» байршилд орлоо`);
      await qc.invalidateQueries({ queryKey: ['inbound'] });
      await qc.invalidateQueries({ queryKey: ['transfers'] });
    },
  });

  const t = q.data?.data;
  const open = t?.status === 'IN_TRANSIT' && !readOnly;
  const arrived = t ? t.items.length - missing.size : 0;

  const toggle = (itemId: string) =>
    setMissing((prev) => {
      const next = new Set(prev);
      if (next.has(itemId)) next.delete(itemId);
      else next.add(itemId);
      return next;
    });

  return (
    <Modal
      wide
      title={t ? `Хүлээн авалт ${t.code}` : 'Хүлээн авалт'}
      subtitle={t ? `${t.fromStore.name} → ${t.toStore.name} · ${formatQty(t.sentCount)} гутал` : undefined}
      onClose={onClose}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>{open ? 'Болих' : 'Хаах'}</Button>
          {t && open ? (
            <Button onClick={() => receive.mutate()} disabled={receive.isPending}>
              {receive.isPending ? 'Хүлээн авч байна…' : `Хүлээн авах (${formatQty(arrived)})`}
            </Button>
          ) : null}
          {t && (t.status === 'RECEIVED' || t.status === 'PARTIAL') ? <ReceiptButton t={t} /> : null}
        </>
      }
    >
      {q.isPending ? <Spinner /> : q.error ? (
        <ErrorNote>{(q.error as Error).message}</ErrorNote>
      ) : t ? (
        <>
          {result ? <OkNote>{result}</OkNote> : null}
          {receive.error ? <ErrorNote>{(receive.error as Error).message}</ErrorNote> : null}
          <TransferFacts t={t} />

          {open ? (
            <p className="text-[12.5px] text-ink-2">
              Гутал бүрийг уншуулах шаардлагагүй — «Хүлээн авах» дармагц бүгд салбарын нөөцөд орно.
              Ирээгүй гутал байвал тэмдэглэгээг нь арилгана: тэр нь замд үлдэж, шилжүүлэг
              «Дутуу ирсэн» болно.
            </p>
          ) : null}

          <ItemsTable
            t={t}
            missing={open ? missing : null}
            onToggle={toggle}
            onToggleAll={(all) => setMissing(all ? new Set() : new Set(t.items.map((l) => l.item.id)))}
          />

          {open && missing.size > 0 ? (
            <>
              <ErrorNote>
                {formatQty(missing.size)} гутал ирээгүй гэж тэмдэглэгдлээ — тэдгээр нь замд үлдэнэ.
              </ErrorNote>
              <Field label="Тэмдэглэл (дутуугийн шалтгаан)">
                <Input value={note} onChange={(e) => setNote(e.target.value)} maxLength={500} />
              </Field>
            </>
          ) : null}
        </>
      ) : null}
    </Modal>
  );
}

/** Хэзээ, хэн илгээж, хэн хүлээн авсан. */
function TransferFacts({ t }: { t: TransferDetail }) {
  const received = t.status === 'RECEIVED' || t.status === 'PARTIAL';
  return (
    <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-[12.5px] text-ink-2">
      <TransferStatusBadge status={t.status} />
      <span>Илгээсэн: {formatDateTime(t.sentAt)}{t.sentBy ? ` · ${t.sentBy.fullName}` : ''}</span>
      {received ? (
        <>
          <span>Хүлээн авсан: {formatDateTime(t.receivedAt)}{t.receivedBy ? ` · ${t.receivedBy.fullName}` : ''}</span>
          <span>
            Ирсэн: <span className="num">{formatQty(t.receivedCount)}</span> /{' '}
            <span className="num">{formatQty(t.sentCount)}</span>
          </span>
        </>
      ) : null}
    </div>
  );
}

/**
 * Гутлын жагсаалт. `missing` өгвөл (хүлээн авах үед) мөр бүр «ирсэн»
 * тэмдэглэгээтэй — анхдагчаар бүгд ирсэн. Хүлээн авсны дараа төлөв.
 */
function ItemsTable({ t, missing, onToggle, onToggleAll }: {
  t: TransferDetail;
  missing: ReadonlySet<string> | null;
  onToggle: (itemId: string) => void;
  onToggleAll: (all: boolean) => void;
}) {
  return (
    <div className="table-pin max-h-[46dvh] overflow-auto rounded-card border border-hair">
      <table className="w-full border-collapse text-[13px]">
        <thead className="sticky top-0 bg-well-2">
          <tr>
            {missing ? (
              <Th>
                <input
                  type="checkbox"
                  aria-label="Бүгд ирсэн"
                  checked={missing.size === 0}
                  onChange={(e) => onToggleAll(e.target.checked)}
                  className="size-4 accent-accent"
                />
              </Th>
            ) : null}
            <Th>Баркод</Th>
            <Th num>Размер</Th>
            <Th>Загвар</Th>
            <Th num>Үнэ</Th>
            {missing ? null : <Th>Төлөв</Th>}
          </tr>
        </thead>
        <tbody>
          {t.items.map((l) => (
            <tr
              key={l.id}
              onClick={missing ? () => onToggle(l.item.id) : undefined}
              className={`transition hover:bg-row-hover ${missing ? 'cursor-pointer' : ''}
                ${missing?.has(l.item.id) ? 'opacity-55' : ''}`}
            >
              {missing ? (
                <Td>
                  <input
                    type="checkbox"
                    aria-label="Ирсэн"
                    checked={!missing.has(l.item.id)}
                    onChange={() => onToggle(l.item.id)}
                    onClick={(e) => e.stopPropagation()}
                    className="size-4 accent-accent"
                  />
                </Td>
              ) : null}
              <Td><span className="num whitespace-nowrap">{formatSerialDisplay(l.item.serial)}</span></Td>
              <Td num>{formatSize(l.item.size)}</Td>
              <Td className="text-ink-2">{l.item.model.name}</Td>
              <Td num><Money value={l.item.priceMnt} /></Td>
              {missing ? null : (
                <Td>
                  {l.scannedIn ? 'Ирсэн' : (
                    <span className="text-alert-ink">Дутуу · {l.item.location?.name ?? '—'}</span>
                  )}
                </Td>
              )}
            </tr>
          ))}
        </tbody>
      </table>
      {t.items.length === 0 ? <Empty>Гутал алга</Empty> : null}
    </div>
  );
}

/** Хүлээн авалтын баримт — Excel (.xlsx). */
function ReceiptButton({ t }: { t: Pick<TransferRow, 'id' | 'code'> }) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const run = async () => {
    setBusy(true);
    setError(null);
    try {
      await download(`/transfers/${t.id}/receipt`, `receipt_${t.code}.xlsx`);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      {error ? <span role="alert" className="text-meta text-alert-ink">{error}</span> : null}
      <Button variant="ghost" onClick={() => void run()} disabled={busy}>
        {busy ? 'Бэлдэж байна…' : 'Excel татах'}
      </Button>
    </>
  );
}

// ------------------------------------------------------------ Багана
const codeColumn: Column<TransferRow> = {
  key: 'code',
  header: 'Дугаар',
  mobile: 'title',
  nowrap: true,
  cell: (t) => <span className="num">{t.code}</span>,
};

// Салбарын кирилл нэр — `nowrap` тавихгүй, боогдоно
const routeColumns: Column<TransferRow>[] = [
  { key: 'from', header: 'Хаанаас', cell: (t) => <span className="text-ink-2 text-pretty">{t.fromStore.name}</span> },
  { key: 'to', header: 'Салбар', cell: (t) => <span className="text-ink-2 text-pretty">{t.toStore.name}</span> },
];

function incomingColumns(): Column<TransferRow>[] {
  return [
    codeColumn,
    ...routeColumns,
    { key: 'count', header: 'Гутал', align: 'right', mobile: 'value', cell: (t) => formatQty(t.sentCount) },
    {
      key: 'sent',
      header: 'Илгээсэн',
      align: 'right',
      cell: (t) => <span className="text-ink-2">{formatDateTime(t.sentAt)}</span>,
    },
    { key: 'by', header: 'Илгээгч', cell: (t) => <span className="text-ink-2">{t.sentBy?.fullName ?? '—'}</span> },
  ];
}

function doneColumns(): Column<TransferRow>[] {
  return [
    codeColumn,
    ...routeColumns,
    {
      key: 'count',
      header: 'Ирсэн / Илгээсэн',
      align: 'right',
      mobile: 'value',
      cell: (t) => (
        <span className={t.status === 'PARTIAL' ? 'text-alert-ink' : ''}>
          {formatQty(t.receivedCount)} / {formatQty(t.sentCount)}
        </span>
      ),
    },
    { key: 'status', header: 'Төлөв', cell: (t) => <TransferStatusBadge status={t.status} /> },
    {
      key: 'at',
      header: 'Хүлээн авсан',
      align: 'right',
      cell: (t) => <span className="text-ink-2">{formatDateTime(t.receivedAt)}</span>,
    },
    { key: 'by', header: 'Хүлээн авагч', cell: (t) => <span className="text-ink-2">{t.receivedBy?.fullName ?? '—'}</span> },
  ];
}
